import React, { useState, useEffect, useCallback } from 'react';
import { Container, Form, Spinner, Table, Button } from 'react-bootstrap';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
} from 'recharts';
import axios from '../api/axios';
import PropTypes from 'prop-types';

const FILTER_OPTIONS = [
    { label: 'Total Sales', value: 'totalRevenue' },
    { label: 'Net Sales', value: 'netSaleRevenue' },
    { label: 'Total Orders', value: 'totalOrders' },
];

const SORT_OPTIONS = [
    { label: 'Top 10 Outlets', value: '-1' },
    { label: 'Bottom 10 Outlets', value: '1' },
];

const DATE_FILTER_OPTIONS = [
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: 'Last 7 Days', value: 'last7days' },
    { label: 'Last 30 Days', value: 'last30days' },
    { label: 'This Month', value: 'thisMonth' },
    { label: 'Last Month', value: 'lastMonth' },
];

const TABLE_COLUMNS = [
    { key: 'outletName', label: 'Outlet Name', sortable: true, isNumeric: false, type: 'text' },

    { key: 'totalOrders', label: 'Orders', sortable: true, isNumeric: true, type: 'count' },
    { key: 'totalRevenue', label: 'Sales', sortable: true, isNumeric: true, type: 'amount' },
    { key: 'netSaleRevenue', label: 'Net Sales', sortable: true, isNumeric: true, type: 'amount' },

    { key: 'appOrders', label: 'App Orders', sortable: true, isNumeric: true, type: 'count' },
    { key: 'appNetSaleRevenue', label: 'App Net Revenue', sortable: true, isNumeric: true, type: 'amount' },

    { key: 'kioskOrders', label: 'Kiosk Orders', sortable: true, isNumeric: true, type: 'count' },
    { key: 'kioskNetSale', label: 'Kiosk Net Sale', sortable: true, isNumeric: true, type: 'amount' },
];

// ── Summary Card Definitions ──────────────────────────────────────────────────
const SUMMARY_CARDS = [
    {
        key: 'totalRevenue',
        label: 'Total Sales',
        icon: 'bi-currency-rupee',
        color: '#4d8af0',
        bg: '#eef3ff',
        prefix: '₹',
    },
    {
        key: 'netSaleRevenue',
        label: 'Taxable (Net Sales)',
        icon: 'bi-receipt',
        color: '#20c997',
        bg: '#e6faf5',
        prefix: '₹',
    },
    {
        key: 'kioskNetSale',
        label: 'Kiosk Net Sales',
        icon: 'bi-display',
        color: '#fd7e14',
        bg: '#fff3e6',
        prefix: '₹',
    },
    {
        key: 'appNetSaleRevenue',
        label: 'App Net Sales',
        icon: 'bi-phone',
        color: '#6f42c1',
        bg: '#f3eeff',
        prefix: '₹',
    },
];

// const formatCardValue = (val) => {
//     if (val === undefined || val === null) return '0.00';
//     const num = Number(val);
//     if (num >= 100000) return `${(num / 100000).toFixed(2)}L`;
//     if (num >= 1000) return `${(num / 1000).toFixed(2)}K`;
//     return num.toFixed(2);
// };

const formatCardValue = (val) => {
    if (val === undefined || val === null) return '₹0.00';

    const num = Number(val);

    return `${num.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};

const OutletRevenueKpi = ({ outletFilter, loadingOutlets }) => {
    const token = localStorage.getItem('authToken');

    const [viewMode, setViewMode] = useState('list');
    const [filterType, setFilterType] = useState('totalRevenue');
    const [sortType, setSortType] = useState('-1');
    const [dateFilter, setDateFilter] = useState('yesterday');
    const [outletData, setOutletData] = useState([]);
    const [totalsData, setTotalsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    const fetchOutletRevenueStats = useCallback(async () => {
        try {
            setLoading(true);
            let url = `dashboard/getAllOutletRevenueStats?filter=${filterType}&sort=${sortType}&dateFilter=${dateFilter}`;
            if (outletFilter && outletFilter !== 'all') {
                url += `&outletId=${outletFilter}`;
            }
            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response?.data?.status === true) {
                const inner = response?.data?.data?.data;
                setOutletData(inner?.outletSales || []);
                setTotalsData(inner?.totalRevenueOfAllOutlets || null);
            }
        } catch (error) {
            console.error('Error fetching outlet revenue stats:', error);
        } finally {
            setLoading(false);
        }
    }, [token, filterType, sortType, dateFilter, outletFilter]);

    useEffect(() => {
        fetchOutletRevenueStats();
    }, [fetchOutletRevenueStats]);

    const truncateName = (name, maxLen = 14) =>
        name?.length > maxLen ? name.slice(0, maxLen) + '...' : name;

    const chartData = outletData?.map((outlet) => ({
        name: truncateName(outlet?.outletName || ''),
        fullName: outlet?.outletName || '',
        value: outlet?.[filterType] || 0,
    }));

    const handleTableSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const sortedData = [...outletData].sort((a, b) => {
        if (!sortConfig.key) return 0;
        const valA = a?.[sortConfig.key] ?? '';
        const valB = b?.[sortConfig.key] ?? '';
        if (typeof valA === 'string') {
            return sortConfig.direction === 'asc'
                ? valA.localeCompare(valB)
                : valB.localeCompare(valA);
        }
        return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
    });

    const selectedFilterLabel =
        FILTER_OPTIONS.find((f) => f.value === filterType)?.label || 'Total Sales';

    // const formatCell = (col, outlet) => {
    //     const val = outlet?.[col.key];

    //     if (!col.isNumeric) return val || '—';
    //     if (val === undefined || val === null) return col.type === 'amount' ? '₹0.00' : '0';

    //     if (col.type === 'amount') {
    //         return `₹${Number(val).toFixed(2)}`;
    //     }

    //     return Number.isInteger(val) ? val : Number(val).toFixed(0);
    // };

    const formatCell = (col, outlet) => {
        const val = outlet?.[col.key];

        if (!col.isNumeric) return val || '—';
        if (val === undefined || val === null) return col.type === 'amount' ? '₹0.00' : '0';

        if (col.type === 'amount') {
            return `₹${Number(val).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })}`;
        }

        return Number.isInteger(val) ? val : Number(val).toFixed(0);
    };

    // const formatTotalCell = (col) => {
    //     if (!totalsData || !col.isNumeric) return '—';

    //     const val = totalsData?.[col.key];
    //     if (val === undefined || val === null) return col.type === 'amount' ? '₹0.00' : '0';

    //     if (col.type === 'amount') {
    //         return `₹${Number(val).toFixed(2)}`;
    //     }

    //     return Number.isInteger(val) ? val : Number(val).toFixed(0);
    // };

    const formatTotalCell = (col) => {
        if (!col.isNumeric) return '—';

        const val = calculatedTotals?.[col.key];

        if (val === undefined || val === null) {
            return col.type === 'amount' ? '₹0.00' : '0';
        }

        if (col.type === 'amount') {
            return `₹${Number(val).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })}`;
        }

        return Number.isInteger(val) ? val : Number(val).toFixed(0);
    };

    // const CustomTooltip = ({ active, payload }) => {
    //     if (active && payload && payload.length) {
    //         const val = payload[0]?.value;
    //         return (
    //             <div style={{
    //                 background: '#fff',
    //                 border: '1px solid #e0e0e0',
    //                 borderRadius: 8,
    //                 padding: '8px 14px',
    //                 boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    //             }}>
    //                 <p className="mb-1 fw-semibold" style={{ fontSize: 13 }}>
    //                     {payload[0]?.payload?.fullName}
    //                 </p>
    //                 <p className="mb-0 text-primary" style={{ fontSize: 13 }}>
    //                     {selectedFilterLabel}:{' '}
    //                     <strong>
    //                         {filterType === 'totalOrders' ? val : Number(val).toFixed(2)}
    //                     </strong>
    //                 </p>
    //             </div>
    //         );
    //     }
    //     return null;
    // };


    const isAmount = selectedFilterLabel?.toLowerCase().includes("amount");

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const val = payload[0]?.value;

            const isCount = filterType === 'totalOrders'; // count case

            return (
                <div style={{
                    background: '#fff',
                    border: '1px solid #e0e0e0',
                    borderRadius: 8,
                    padding: '8px 14px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}>
                    <p className="mb-1 fw-semibold" style={{ fontSize: 13 }}>
                        {payload[0]?.payload?.fullName}
                    </p>

                    <p className="mb-0 text-primary" style={{ fontSize: 13 }}>
                        {selectedFilterLabel}:{' '}
                        <strong>
                            {isCount
                                ? val
                                :
                                `₹${Number(val).toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })}`
                            }
                        </strong>
                    </p>
                </div>
            );
        }
        return null;
    };

    const calculatedTotals = outletData.reduce((acc, outlet) => {
        TABLE_COLUMNS.forEach((col) => {
            if (col.isNumeric) {
                acc[col.key] = (acc[col.key] || 0) + (Number(outlet[col.key]) || 0);
            }
        });
        return acc;
    }, {});

    return (
        <Container fluid className="py-3 shadow-sm" style={{ backgroundColor: 'white' }}>
            <div className="d-flex justify-content-between align-items-center mb-1 mt-0">
                <div>
                    <h4 className="fw-bold mb-0">OUTLET STATISTICS</h4>
                </div>
                <div className="d-flex align-items-center bg-light p-1 ps-2 rounded">
                    <i className="bi bi-calendar-event me-2 text-primary" />
                    <Form.Select
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.target.value)}
                        style={{ width: '145px' }}
                        className="border-0 bg-light"
                    >
                        {DATE_FILTER_OPTIONS?.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </Form.Select>
                </div>
            </div>
            <hr />

            {/* ── SUMMARY CARDS ─────────────────────────────────────────────── */}
            <div className="row g-3 mb-4">
                {SUMMARY_CARDS.map((card) => (
                    <div key={card.key} className="col-6 col-md-3">
                        <div
                            style={{
                                background: card.bg,
                                borderRadius: 12,
                                padding: '16px 18px',
                                borderLeft: `4px solid ${card.color}`,
                                minHeight: 100,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                            }}
                        >
                            <div className="d-flex justify-content-between align-items-start">
                                <span
                                    style={{
                                        fontSize: 11,
                                        fontWeight: 600,
                                        color: '#666',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.4px',
                                        lineHeight: 1.3,
                                    }}
                                >
                                    {card.label}
                                </span>
                                <i
                                    className={`bi ${card.icon}`}
                                    style={{ fontSize: 18, color: card.color, opacity: 0.85 }}
                                />
                            </div>

                            {loading || loadingOutlets ? (
                                <Spinner animation="border" size="sm" style={{ color: card.color, marginTop: 8 }} />
                            ) : (
                                <div
                                    style={{
                                        fontSize: 20,
                                        fontWeight: 600,
                                        color: '#1a1a2e',
                                        marginTop: 6,
                                        letterSpacing: '-0.5px',
                                    }}
                                >
                                    {card.prefix}
                                    {formatCardValue(totalsData?.[card.key])}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
            {/* ──────────────────────────────────────────────────────────────── */}

            {/* Top Bar: Date Filter + Graph/List Toggle */}
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                {/* Date Filter */}
                <div className="d-flex align-items-center p-2 rounded">
                    {/* <i className="bi bi-calendar-event me-2 text-primary" />
                    <Form.Select
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.target.value)}
                        style={{ width: '145px' }}
                        className="border-0 bg-light"
                    >
                        {DATE_FILTER_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </Form.Select> */}
                </div>

                {/* Graph / List Toggle */}
                <div
                    className="d-flex"
                    style={{ border: '1px solid #dee2e6', borderRadius: 8, overflow: 'hidden' }}
                >
                    <Button
                        variant={viewMode === 'list' ? 'primary' : 'light'}
                        className="rounded-0 px-4"
                        style={{ border: 'none', fontWeight: viewMode === 'list' ? 600 : 400 }}
                        onClick={() => setViewMode('list')}
                    >
                        {viewMode === 'list' && <i className="bi bi-check2 me-1" />}
                        List
                    </Button>
                    <Button
                        variant={viewMode === 'graph' ? 'primary' : 'light'}
                        className="rounded-0 px-4"
                        style={{ border: 'none', fontWeight: viewMode === 'graph' ? 600 : 400 }}
                        onClick={() => setViewMode('graph')}
                    >
                        {viewMode === 'graph' && <i className="bi bi-check2 me-1" />}
                        Graph
                    </Button>
                </div>
            </div>

            {/* ── GRAPH VIEW ── */}
            {viewMode === 'graph' && (
                <>
                    <div className="d-flex gap-2 mb-3 flex-wrap">
                        <Form.Select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            style={{ width: '160px' }}
                            className="border rounded"
                        >
                            {FILTER_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </Form.Select>

                        <Form.Select
                            value={sortType}
                            onChange={(e) => setSortType(e.target.value)}
                            style={{ width: '180px' }}
                            className="border rounded"
                        >
                            {SORT_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </Form.Select>
                    </div>

                    <div className="bg-light p-3 rounded shadow-sm">
                        {loading || loadingOutlets ? (
                            <div className="d-flex justify-content-center align-items-center" style={{ height: 300 }}>
                                <Spinner animation="border" variant="primary" />
                            </div>
                        ) : chartData.length === 0 ? (
                            <div className="d-flex justify-content-center align-items-center text-muted" style={{ height: 300 }}>
                                No data available
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height={500}>
                                <BarChart
                                    data={chartData}
                                    barSize={80}

                                    margin={{ top: 20, right: 20, left: 10, bottom: 20 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e0e0" />
                                    <XAxis
                                        dataKey="name"
                                        tick={{ fontSize: 11, fill: '#666', fontWeight: 600 }}
                                        angle={-20}
                                        textAnchor="end"
                                        interval={0}
                                    />
                                    {/* <YAxis
                                        tick={{ fontSize: 11, fill: '#666', fontWeight: 600 }}
                                        tickFormatter={(v) =>
                                            v >= 1000 ? `${(v / 1000).toFixed(1)}K` : v
                                        }
                                    /> */}
                                    <YAxis
                                        tick={{ fontSize: 11, fill: '#666', fontWeight: 600 }}
                                        tickFormatter={(v) => {
                                            if (isAmount) {
                                                return v >= 1000
                                                    ? `₹${(v / 1000).toFixed(1)}K`
                                                    : `₹${v}`;
                                            }
                                            return v;
                                        }}
                                    />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend
                                        verticalAlign="bottom"
                                        wrapperStyle={{ paddingTop: 40 }}
                                        formatter={() => selectedFilterLabel}
                                    />
                                    <Bar
                                        dataKey="value"
                                        fill="#4d8af0"
                                        radius={[4, 4, 0, 0]}
                                        // name={selectedFilterLabel}
                                        name={isAmount ? "Amount (₹)" : selectedFilterLabel}
                                        animationDuration={800}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </>
            )}

            {/* ── LIST VIEW ── */}
            {viewMode === 'list' && (
                <div style={{ overflowX: 'auto' }}>
                    {loading || loadingOutlets ? (
                        <div className="d-flex justify-content-center align-items-center" style={{ height: 300 }}>
                            <Spinner animation="border" variant="primary" />
                        </div>
                    ) : (
                        <table
                            className="w-100"
                            style={{
                                borderCollapse: 'separate',
                                borderSpacing: 0,
                                fontSize: 13
                            }}
                        >
                            <thead>
                                <tr>
                                    {TABLE_COLUMNS?.map((col, index) => (
                                        <th
                                            key={col.key}
                                            style={{
                                                padding: '14px 16px',
                                                fontSize: 12,
                                                fontWeight: 600,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.3px',
                                                color: '#6b7280',
                                                textAlign: col.type === 'amount' ? 'right' : col.type === 'count' ? 'center' : 'left',
                                                borderBottom: '1px solid #e5e7eb',
                                                backgroundColor: '#ffffff',
                                                whiteSpace: 'nowrap',
                                                cursor: col.sortable ? 'pointer' : 'default',
                                            }}
                                            onClick={() => col.sortable && handleTableSort(col.key)}
                                        >
                                            <span style={{
                                                display: 'flex', alignItems: 'center', gap: '6px', justifyContent:
                                                    col.type === 'amount' ? 'flex-end' :
                                                        col.type === 'count' ? 'center' :
                                                            'flex-start'
                                            }}>
                                                {col.label}
                                                {col.sortable && (
                                                    <span style={{
                                                        color: sortConfig.key === col.key ? '#2563eb' : '#d1d5db',
                                                        fontSize: 11
                                                    }}>
                                                        {sortConfig.key === col.key
                                                            ? sortConfig.direction === 'asc' ? '▲' : '▼'
                                                            : '◆'}
                                                    </span>
                                                )}
                                            </span>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {/* Totals Row */}
                                {totalsData && (
                                    <tr style={{
                                        backgroundColor: '#f9fafb',
                                        borderBottom: '1px solid #e5e7eb'
                                    }}>
                                        <td style={{
                                            padding: '12px 16px',
                                            fontWeight: 600,
                                            color: '#111827',
                                            borderBottom: '1px solid #e5e7eb'
                                        }}>
                                            Total
                                        </td>
                                        {TABLE_COLUMNS.slice(1).map((col) => (
                                            <td
                                                key={col.key}
                                                style={{
                                                    padding: '12px 16px',
                                                    fontWeight: 600,
                                                    color: '#111827',
                                                    textAlign: col.type === 'amount' ? 'right' : col.type === 'count' ? 'center' : 'left',
                                                    borderBottom: '1px solid #e5e7eb'
                                                }}
                                            >
                                                {formatTotalCell(col)}
                                            </td>
                                        ))}
                                    </tr>
                                )}

                                {/* Data Rows */}
                                {sortedData.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan={TABLE_COLUMNS.length}
                                            style={{
                                                padding: '40px 16px',
                                                textAlign: 'center',
                                                color: '#9ca3af'
                                            }}
                                        >
                                            No outlet data available
                                        </td>
                                    </tr>
                                ) : (
                                    sortedData.map((outlet, idx) => (
                                        <tr
                                            key={outlet?._id || idx}
                                            style={{
                                                borderBottom: '1px solid #f3f4f6',
                                                transition: 'background-color 0.15s'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = '#fafafa';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = 'transparent';
                                            }}
                                        >
                                            <td style={{
                                                padding: '12px 16px',
                                                color: '#2563eb',
                                                fontWeight: 500,
                                                cursor: 'pointer'
                                            }}>
                                                {outlet?.outletName || '—'}
                                            </td>
                                            {TABLE_COLUMNS.slice(1).map((col) => (
                                                <td
                                                    key={col.key}
                                                    style={{
                                                        padding: '12px 16px',
                                                        color: '#374151',
                                                        textAlign: col.type === 'amount' ? 'right' : col.type === 'count' ? 'center' : 'left',
                                                        fontWeight: col.type === 'amount' ? 500 : 400
                                                    }}
                                                >
                                                    {formatCell(col, outlet)}
                                                </td>
                                            ))}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

        </Container>
    );
};

OutletRevenueKpi.propTypes = {
    outletFilter: PropTypes.string.isRequired,
    loadingOutlets: PropTypes.bool.isRequired,
};

export default OutletRevenueKpi;